import React, { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEdit, faTrash, faPlus } from "@fortawesome/free-solid-svg-icons";
import { toast } from "react-toastify";

const AssessmentCategoryCards = ({
  selectedLevel,
  allCategories = [],
  onConfirm,
  onCategoriesChange,
}) => {
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [currentCategory, setCurrentCategory] = useState(null);
  const [newCategory, setNewCategory] = useState({
    category_id: "",
    category_name: "",
    questions_count: "",
    duration: "30",
    difficulty: "Easy",
  });

  // Initialize from selectedLevel if it has categories
  useEffect(() => {
    if (selectedLevel && selectedLevel.categories) {
      setSelectedCategories(selectedLevel.categories);
    } else {
      // Clear categories if selectedLevel has no categories or is null
      setSelectedCategories([]);
    }
  }, [selectedLevel]);

  const handleEdit = (category) => {
    setCurrentCategory({ ...category });
    setIsEditModalOpen(true);
  };

  const handleDelete = (id) => {
    const updatedCategories = selectedCategories.filter((cat) => cat.id !== id);
    setSelectedCategories(updatedCategories);
    // Sync to parent
    if (onCategoriesChange) {
      onCategoriesChange(updatedCategories);
    }
    toast.success("Category removed");
  };

  const handleUpdate = () => {
    if (!currentCategory.questions_count || !currentCategory.duration) {
      toast.error("Please fill all fields");
      return;
    }

    const updatedCategories = selectedCategories.map((cat) =>
      cat.id === currentCategory.id ? currentCategory : cat
    );
    setSelectedCategories(updatedCategories);
    // Sync to parent
    if (onCategoriesChange) {
      onCategoriesChange(updatedCategories);
    }
    setIsEditModalOpen(false);
    setCurrentCategory(null);
    toast.success("Category updated");
  };

  const handleAddCategory = () => {
    if (
      !newCategory.category_id ||
      !newCategory.questions_count ||
      !newCategory.duration
    ) {
      toast.error("Please select a category and fill all fields");
      return;
    }

    // Find the full category data from allCategories
    const selectedCat = allCategories.find(
      (cat) => cat.id === parseInt(newCategory.category_id)
    );

    if (!selectedCat) {
      toast.error("Category not found");
      return;
    }

    const categoryToAdd = {
      id: Date.now(), // Unique ID for local management
      category_id: selectedCat.id,
      category_name: selectedCat.category_name,
      questions_count: parseInt(newCategory.questions_count),
      duration: newCategory.duration,
      difficulty: newCategory.difficulty,
    };

    const updatedCategories = [...selectedCategories, categoryToAdd];
    setSelectedCategories(updatedCategories);
    // Sync to parent
    if (onCategoriesChange) {
      onCategoriesChange(updatedCategories);
    }
    setIsAddModalOpen(false);
    setNewCategory({
      category_id: "",
      category_name: "",
      questions_count: "",
      duration: "30",
      difficulty: "Easy",
    });
    toast.success("Category added");
  };

  if (!selectedLevel) {
    return (
      <div className="flex items-center justify-center h-full">
        <p className="text-gray-500">
          Select an Assessment level to configure categories
        </p>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-4">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">
            Assessment Configuration
          </h1>
          <p className="text-gray-600 text-sm mt-1">
            Configure categories and questions for:{" "}
            <span className="font-semibold">{selectedLevel.level_name}</span>
          </p>
        </div>
        <button
          className="bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 font-semibold transition"
          onClick={() => setIsAddModalOpen(true)}
        >
          <FontAwesomeIcon icon={faPlus} />
          Add Category
        </button>
      </div>

      {selectedCategories.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
          <p className="text-gray-500 text-lg mb-4">
            No categories configured yet
          </p>
        </div>
      ) : (
        <div
          className={`grid ${selectedCategories.length === 1
            ? "grid-cols-1"
            : "grid-cols-1 md:grid-cols-2"
            } gap-4`}
        >
          {selectedCategories.map((category) => (
            <div
              key={category.id}
              className="bg-white shadow-lg rounded-lg p-5 border-l-4 border-blue-500 hover:shadow-xl transition-shadow"
            >
              <div className="flex items-start mb-3">
                <span className="font-bold text-lg text-gray-800">
                  {category.category_name}
                </span>
                <div className="flex gap-2">
                  <button
                    onClick={() => handleEdit(category)}
                    className="text-blue-500 hover:text-blue-700 hover:bg-blue-50 p-2 rounded transition"
                    title="Edit"
                  >
                    <FontAwesomeIcon icon={faEdit} />
                  </button>
                  <button
                    onClick={() => handleDelete(category.id)}
                    className="text-red-500 hover:text-red-700 hover:bg-red-50 p-2 rounded transition"
                    title="Delete"
                  >
                    <FontAwesomeIcon icon={faTrash} />
                  </button>
                </div>
              </div>

              <div className="flex gap-4 text-sm">
                <div className="flex items-center gap-2">
                  {/* <span className="text-gray-600">Duration:</span> */}
                  <span className="bg-blue-500 text-white text-xs font-semibold px-3 py-1 rounded">
                    {category.duration} mins
                  </span>
                </div>

                <div
                  className={`flex items-center gap-2 ${category.difficulty === "Easy"
                    ? "bg-green-500"
                    : category.difficulty === "Medium"
                      ? "bg-yellow-500"
                      : "bg-red-500"
                    }`}
                >
                  {/* <span className="text-gray-600">Difficulty:</span> */}
                  <span className="px-3 py-1 text-white text-xs font-semibold rounded">
                    {category.difficulty}
                  </span>
                </div>

                <div className="flex items-center pt-2 gap-2">
                  <span className="text-gray-600">Questions:</span>
                  <span className="bg-blue-100 text-blue-800 text-xs font-bold px-3 py-1 rounded">
                    {category.questions_count}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Edit Modal */}
      {isEditModalOpen && currentCategory && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md shadow-2xl">
            <h2 className="text-xl font-bold mb-4 text-gray-800">
              Edit Category
            </h2>

            <div className="space-y-4">
              <div>
                <label className="block mb-2 font-semibold text-gray-700">
                  Category Name
                </label>
                <input
                  type="text"
                  value={currentCategory.category_name}
                  disabled
                  className="border border-gray-300 rounded p-2 w-full bg-gray-100 text-gray-600"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block mb-2 font-semibold text-gray-700">
                    Duration (mins)
                  </label>
                  <input
                    type="number"
                    value={currentCategory.duration}
                    onChange={(e) =>
                      setCurrentCategory({
                        ...currentCategory,
                        duration: e.target.value,
                      })
                    }
                    className="border border-gray-300 rounded p-2 w-full focus:outline-none focus:border-blue-500"
                    placeholder="30"
                  />
                </div>

                <div>
                  <label className="block mb-2 font-semibold text-gray-700">
                    Questions Count
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="100"
                    value={currentCategory.questions_count}
                    onChange={(e) =>
                      setCurrentCategory({
                        ...currentCategory,
                        questions_count: e.target.value,
                      })
                    }
                    className="border border-gray-300 rounded p-2 w-full focus:outline-none focus:border-blue-500"
                    placeholder="10"
                  />
                </div>
              </div>

              <div>
                <label className="block mb-2 font-semibold text-gray-700">
                  Difficulty
                </label>
                <select
                  value={currentCategory.difficulty}
                  onChange={(e) =>
                    setCurrentCategory({
                      ...currentCategory,
                      difficulty: e.target.value,
                    })
                  }
                  className="border border-gray-300 rounded p-2 w-full focus:outline-none focus:border-blue-500"
                >
                  <option value="Easy">Easy</option>
                  <option value="Medium">Medium</option>
                  <option value="High">High</option>
                </select>
              </div>
            </div>

            <div className="flex justify-end gap-2 mt-6">
              <button
                className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600 transition"
                onClick={() => setIsEditModalOpen(false)}
              >
                Cancel
              </button>
              <button
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition font-semibold"
                onClick={handleUpdate}
              >
                Update
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Modal */}
      {isAddModalOpen && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md shadow-2xl">
            <h2 className="text-xl font-bold mb-4 text-gray-800">
              Add Category
            </h2>

            <div className="space-y-4">
              <div>
                <label className="block mb-2 font-semibold text-gray-700">
                  Category Name
                </label>
                <select
                  value={newCategory.category_id}
                  onChange={(e) => {
                    const selectedCat = allCategories.find(
                      (cat) => cat.id === parseInt(e.target.value)
                    );
                    setNewCategory({
                      ...newCategory,
                      category_id: e.target.value,
                      category_name: selectedCat
                        ? selectedCat.category_name
                        : "",
                    });
                  }}
                  className="border border-gray-300 rounded p-2 w-full focus:outline-none focus:border-blue-500"
                >
                  <option value="">-- Select a Category --</option>
                  {allCategories.map((cat) => (
                    <option key={cat.id} value={cat.id}>
                      {cat.category_name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block mb-2 font-semibold text-gray-700">
                    Duration (mins)
                  </label>
                  <input
                    type="number"
                    value={newCategory.duration}
                    onChange={(e) =>
                      setNewCategory({
                        ...newCategory,
                        duration: e.target.value,
                      })
                    }
                    className="border border-gray-300 rounded p-2 w-full focus:outline-none focus:border-blue-500"
                    placeholder="30"
                  />
                </div>

                <div>
                  <label className="block mb-2 font-semibold text-gray-700">
                    Questions Count
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="100"
                    value={newCategory.questions_count}
                    onChange={(e) =>
                      setNewCategory({
                        ...newCategory,
                        questions_count: e.target.value,
                      })
                    }
                    className="border border-gray-300 rounded p-2 w-full focus:outline-none focus:border-blue-500"
                    placeholder="10"
                  />
                </div>
              </div>

              <div>
                <label className="block mb-2 font-semibold text-gray-700">
                  Difficulty
                </label>
                <select
                  value={newCategory.difficulty}
                  onChange={(e) =>
                    setNewCategory({
                      ...newCategory,
                      difficulty: e.target.value,
                    })
                  }
                  className="border border-gray-300 rounded p-2 w-full focus:outline-none focus:border-blue-500"
                >
                  <option value="Easy">Easy</option>
                  <option value="Medium">Medium</option>
                  <option value="High">High</option>
                </select>
              </div>
            </div>

            <div className="flex justify-end gap-2 mt-6">
              <button
                className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600 transition"
                onClick={() => setIsAddModalOpen(false)}
              >
                Cancel
              </button>
              <button
                className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 transition font-semibold"
                onClick={handleAddCategory}
              >
                Add Category
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AssessmentCategoryCards;
